import { useState, useCallback } from "react";
import { useRequest } from "../../../api/useRequest";
import { MediaRequestFactory } from "../../../api/requestFactory";
import { notifyMediaChanged } from "./media-events";

export const useMedia = (context = "workspace") => {
  const { requestMaker } = useRequest();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const isOrg = context === "org";

  const listMedia = useCallback(
    async (mediaType, page = 1, perPage = 20, workspaceIds) => {
      try {
        setLoading(true);
        setError(null);
        const request = isOrg
          ? MediaRequestFactory.createOrgListMediaRequest(workspaceIds, mediaType, page, perPage)
          : MediaRequestFactory.createListMediaRequest(mediaType, page, perPage);
        request.disableLoading = true;
        const response = await requestMaker(request);
        return response;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [requestMaker, isOrg]
  );

  const getMedia = useCallback(
    async (id) => {
      try {
        setLoading(true);
        setError(null);
        const request = isOrg
          ? MediaRequestFactory.createOrgGetMediaRequest(id)
          : MediaRequestFactory.createGetMediaRequest(id);
        request.disableLoading = true;
        const response = await requestMaker(request);
        return response;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [requestMaker, isOrg]
  );

  const uploadMedia = useCallback(
    async (file, workspaceId) => {
      try {
        setUploading(true);
        setError(null);

        // Step 1: Get presigned upload URL
        const createRequest = isOrg
          ? MediaRequestFactory.createOrgUploadRequest(workspaceId, file.type, file.name)
          : MediaRequestFactory.createUploadRequest(file.type, file.name);
        createRequest.disableLoading = true;
        const { media_item, upload } = await requestMaker(createRequest);

        // Step 2: Upload file to object storage via multipart POST
        const formData = new FormData();
        Object.entries(upload.fields).forEach(([key, value]) => {
          formData.append(key, value);
        });
        formData.append("file", file);

        const uploadResponse = await fetch(upload.url, {
          method: "POST",
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error("File upload failed");
        }

        // Step 3: Confirm upload
        const confirmRequest = isOrg
          ? MediaRequestFactory.createOrgConfirmUploadRequest(media_item.id)
          : MediaRequestFactory.createConfirmUploadRequest(media_item.id);
        confirmRequest.disableLoading = true;
        const response = await requestMaker(confirmRequest);
        notifyMediaChanged();
        return { ...response, key: upload.key };
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setUploading(false);
      }
    },
    [requestMaker, isOrg]
  );

  const deleteMedia = useCallback(
    async (id) => {
      try {
        setError(null);
        const request = isOrg
          ? MediaRequestFactory.createOrgDeleteMediaRequest(id)
          : MediaRequestFactory.createDeleteMediaRequest(id);
        request.disableLoading = true;
        const response = await requestMaker(request);
        notifyMediaChanged();
        return response;
      } catch (err) {
        setError(err);
        throw err;
      }
    },
    [requestMaker, isOrg]
  );

  const copyMedia = useCallback(
    async (id, targetWorkspaceId) => {
      try {
        setError(null);
        const request = MediaRequestFactory.createOrgCopyMediaRequest(id, targetWorkspaceId);
        request.disableLoading = true;
        const response = await requestMaker(request);
        notifyMediaChanged();
        return response;
      } catch (err) {
        setError(err);
        throw err;
      }
    },
    [requestMaker]
  );

  return {
    listMedia,
    getMedia,
    uploadMedia,
    deleteMedia,
    copyMedia,
    loading,
    uploading,
    error,
  };
};
